import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { MapPin } from "lucide-react";
import { US_STATES } from "@/lib/us-states";
import { US_CITIES_VALID } from "@/data/us-cities";
import { stateNameSlug, toSlug } from "@/lib/seo-slugs";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/locations/")({
  head: () => {
    const title = "Browse Doctors by Location | Heallexa";
    const description =
      "Find healthcare providers by state and city across all 50 U.S. states. Heallexa lists 6.3 million+ verified doctors, dentists, and specialists — searchable by location, specialty, and insurance.";
    const url = `${SITE}/locations`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
          ]),
        ),
      ],
    };
  },
  component: LocationsIndex,
});

function LocationsIndex() {
  const cities = [...US_CITIES_VALID].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Breadcrumbs
          className="mb-6"
          items={[{ name: "Home", href: "/" }, { name: "Locations" }]}
        />
        <Reveal from="up">
          <header className="mb-12">
            <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Heallexa Directory</p>
            <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
              Browse Doctors by Location
            </h1>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[640px] leading-relaxed">
              Search <strong className="text-foreground font-medium">6.3 million+ verified healthcare providers</strong> across
              all 50 U.S. states. Every listing is sourced from the federal NPI registry — the
              authoritative record of licensed U.S. providers. Select a state to browse by city,
              or jump straight to one of {cities.length}+ major cities below.
            </p>
          </header>
        </Reveal>

        <Reveal as="section" from="up" delay={60} className="mb-14">
          <h2 className="font-display text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">All States</h2>
          <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-3 gap-x-6">
            {US_STATES.map((s) => (
              <li key={s.code}>
                <Link
                  to="/locations/$state"
                  params={{ state: stateNameSlug(s.name) }}
                  className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                  <span className="truncate">{s.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal as="section" from="up" delay={60}>
          <h2 className="font-display text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Popular Cities ({cities.length})
          </h2>
          <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-3 gap-x-6">
            {cities.map((c) => {
              const st = US_STATES.find((s) => s.code === c.state);
              if (!st) return null;
              return (
                <li key={`${c.name}-${c.state}`}>
                  <Link
                    to="/locations/$state/$city"
                    params={{ state: stateNameSlug(st.name), city: toSlug(c.name) }}
                    search={{ page: undefined }}
                    className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <span className="truncate">
                      {c.name}, <span className="opacity-70">{c.state}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Reveal>
      </main>
      <FooterSection />
    </div>
  );
}
